import { queryKeyPrefix } from '@src/corelib/query/utils';
import { useMutation } from '@src/query';


/**
 * Storefront-FAQ mutation hooks (#282). Same shape as the canned-response
 * module (`query/leads/useCannedResponseMutations.js`) — the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract; the corelib query singleton is NOT imported here.
 *
 * The list is read with `useQuery('/sellers/faqs/<sellerId>/')`, whose key is
 * derived as `[namespace, ...url.split('/')]`, yielding
 * `['root','','sellers','faqs',sellerId,'']`. The prefix below
 * `['root','','sellers','faqs',sellerId]` matches it (TanStack matches a key by
 * any leading prefix).
 */

/** Cache key prefix for a seller's storefront-FAQ list. */
function sellerFaqsKeyPrefix(sellerId) {
  return queryKeyPrefix('sellers', 'faqs', String(sellerId || ''));
}


/** Create a FAQ entry. Variables: `{ sellerId, question, answer, partType }`.
 * `partType` is optional — null/omitted means "shows on every part". */
function useCreateSellerFaqMutation(params) {
  return useMutation({
    mutateFn: async ({
      apiClient, queryClient, sellerId, question, answer, partType,
    }) => {
      const data = await apiClient.request({
        url: `/sellers/faqs/${sellerId}/`,
        method: 'POST',
        payload: { question, answer, part_type: partType || null },
      });
      queryClient.invalidateQueries({ queryKey: sellerFaqsKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Update a FAQ entry. Variables: `{ sellerId, id, question, answer, partType }`. */
function useUpdateSellerFaqMutation(params) {
  return useMutation({
    mutateFn: async ({
      apiClient, queryClient, sellerId, id, question, answer, partType,
    }) => {
      const data = await apiClient.request({
        url: `/sellers/faqs/${sellerId}/${id}/`,
        method: 'PATCH',
        payload: { question, answer, part_type: partType || null },
      });
      queryClient.invalidateQueries({ queryKey: sellerFaqsKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Delete a FAQ entry. Variables: `{ sellerId, id }`. */
function useDeleteSellerFaqMutation(params) {
  return useMutation({
    mutateFn: async ({
      apiClient, queryClient, sellerId, id,
    }) => {
      const data = await apiClient.request({
        url: `/sellers/faqs/${sellerId}/${id}/`,
        method: 'DELETE',
      });
      queryClient.invalidateQueries({ queryKey: sellerFaqsKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Reorder FAQ entries. Variables: `{ sellerId, orderedIds }` → POST with
 * `{ ordered_ids }`. */
function useReorderSellerFaqMutation(params) {
  return useMutation({
    mutateFn: async ({
      apiClient, queryClient, sellerId, orderedIds,
    }) => {
      const data = await apiClient.request({
        url: `/sellers/faqs/${sellerId}/reorder/`,
        method: 'POST',
        payload: { ordered_ids: orderedIds },
      });
      queryClient.invalidateQueries({ queryKey: sellerFaqsKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


export {
  sellerFaqsKeyPrefix,
  useCreateSellerFaqMutation,
  useDeleteSellerFaqMutation,
  useReorderSellerFaqMutation,
  useUpdateSellerFaqMutation,
};
